import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Values } from '@app-evolution-cms/element-ui';

const STORAGE_THEME_KEY = 'ui-theme';

export const THEME = {
  DARK: 'dark',
  LIGHT: 'light',
  SYSTEM: 'system',
  COLORBLIND: 'colorblind',
  TEMPORAL: 'temporal',
} as const;

export type Theme = Values<typeof THEME>;

interface ThemeProviderProps {
  defaultTheme?: Theme;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeProviderState | undefined>(undefined);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
}: PropsWithChildren<ThemeProviderProps>) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return defaultTheme;
    const storedTheme = localStorage.getItem(STORAGE_THEME_KEY) as Theme | null;
    return storedTheme || defaultTheme;
  });

  const applyTheme = useCallback((currentTheme: Theme) => {
    const resolvedTheme =
      currentTheme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : currentTheme;

    const root = document.documentElement;
    root.classList.remove('light', 'dark', 'colorblind', 'temporal');
    root.classList.add(resolvedTheme);
  }, []);

  useEffect(() => {
    applyTheme(theme);

    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme('system');
      mediaQuery.addEventListener('change', handleChange);

      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, [theme, applyTheme]);

  const setTheme = useCallback((newTheme: Theme) => {
    localStorage.setItem(STORAGE_THEME_KEY, newTheme);
    setThemeState(newTheme);
  }, []);

  const value = { theme, setTheme };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
}
